import type { CashflowKenh, CompanyCashflow } from '@qlk/shared';
import type { ReactNode } from 'react';

import { dinhDangTien, KhoiMuc, Skeleton, soNguyen } from '../../../ui/index.js';
import { tenKy } from '../../../lib/ky.js';

/**
 * Khối "Dòng tiền": gộp − thuế Mỹ − Network − VAT + Content ID nhạc − phí kho
 * nhạc = thực nhận. Theo ảnh `docs/design/doanh-thu.png`, thêm hai ô thuế
 * (11/10/2026) để phép trừ trên màn hình khớp với cột "Thực nhận" của bảng ngay
 * bên dưới — trước đây khối này chỉ trừ phần Network nên cao hơn số thật.
 *
 * ## Vì sao là **sao kê dọc**, không còn dải ngang (16/10/2026)
 *
 * Bản cũ xếp 7 ô và 6 dấu `−/+/=` trên **một hàng** từ 1280px khung nhìn
 * (`xl:flex-row`). Đo thật trên laptop 1280×720: vùng nội dung 992px, trừ 6
 * nhóm dấu (38px mỗi nhóm) còn 764px chia bảy — mỗi ô 109px, trừ padding còn
 * **75–86px cho chữ**. Hậu quả đúng như thấy ở bản chạy thật: nhãn xuống dòng
 * từng từ, ô cao thấp lệch nhau, và hai ô cuối (kể cả "Công ty thực nhận") bị
 * `overflow-hidden` của thẻ cắt mất.
 *
 * Dải ngang đó **không vừa ở bất kỳ khổ nào app có**: nhãn 120px + padding
 * 32px × 7 ô + 228px chỗ cho dấu = **1.292px**, trong khi vùng nội dung rộng
 * nhất là 1.344px (`--rong-noi-dung` 1400 trừ padding 56) và khối này còn nằm
 * trong `KhoiMuc` có padding 20px mỗi bên → tối đa 1.304px. Thừa 12px thì
 * không phải là "vừa", nên bản ngang đã bỏ hẳn thay vì giữ sau một container
 * query không bao giờ bật.
 *
 * Dạng sao kê đọc đúng thứ tự phép tính từ trên xuống, số căn phải nên so được
 * bằng mắt, và không có bề ngang nào làm nó gãy.
 */

interface OTien {
  nhan: string;
  giaTri: ReactNode;
  /** `true` khi ô hiện **chữ** thay cho số — in cỡ chữ phụ, không cỡ số. */
  laChu?: boolean;
  moTa: string;
  nen: string;
  mauChu: string;
  /**
   * Dấu phép tính, **đứng đầu dòng nó tác động**: dòng "thuế Mỹ" bị trừ khỏi
   * dòng "doanh thu gộp" ngay trên nó, nên dấu `−` nằm ở dòng thuế. Dòng đầu
   * không có dấu.
   *
   * Trước đây là một mảng `DAU` song song với mảng ô, đánh số bằng tay. Bản
   * theo kênh được giao có **5 dòng** thay vì 7 (bỏ hai dòng kho nhạc), nên
   * hai mảng song song là chỗ để lệch nhau: dấu thuộc về dòng, cho vào dòng.
   */
  dau?: string;
}

export interface KhoiDongTienProps {
  /**
   * Dòng tiền của cả công ty, **hoặc** dòng tiền chỉ gồm các kênh được xem.
   *
   * Phân biệt bằng `'musicRevenue' in dongTien`: bản theo kênh cố ý không có
   * hai khoản kho nhạc và không có quy đổi VND (xem `cashflowKenhSchema`).
   */
  dongTien: CompanyCashflow | CashflowKenh | undefined;
  /** Số kênh đang có doanh thu trong kỳ, hiện ở dòng mô tả ô đầu tiên. */
  soKenhKiemTien: number;
  /** Khoảng tỷ lệ Network của các kênh, ví dụ "15–30% theo kênh". */
  moTaTyLe: string;
}

export function KhoiDongTien({ dongTien, soKenhKiemTien, moTaTyLe }: KhoiDongTienProps) {
  if (!dongTien) {
    return (
      <section className="mb-6 rounded-the border border-vien bg-be-mat p-6">
        <Skeleton cao="132px" bo="vua" />
      </section>
    );
  }

  const cuaCongTy = 'musicRevenue' in dongTien;
  const chuaNhapPhi = cuaCongTy && Number(dongTien.musicLicenseCost) === 0;

  /* Bốn dòng đầu giống nhau ở cả hai bản: cùng chuỗi trừ, cùng con số. */
  const o: OTien[] = [
    {
      nhan: 'Doanh thu gộp YouTube',
      giaTri: dinhDangTien(dongTien.gross),
      moTa: `${String(soKenhKiemTien)} kênh kiếm tiền`,
      nen: 'var(--color-header-bang)',
      mauChu: 'var(--color-chu)',
    },
    {
      nhan: 'Thuế Mỹ Google giữ',
      giaTri: dinhDangTien(dongTien.thueMy),
      moTa: 'trên phần doanh thu ở Mỹ',
      nen: 'var(--color-bad-nen)',
      mauChu: 'var(--color-bad-chu)',
      dau: '−',
    },
    {
      nhan: 'Phần chia Network',
      giaTri: dinhDangTien(dongTien.partnerShare),
      moTa: moTaTyLe,
      nen: 'var(--color-warn-nen)',
      mauChu: 'var(--color-warn-chu)',
      dau: '−',
    },
    {
      nhan: 'VAT',
      giaTri: dinhDangTien(dongTien.vat),
      moTa: 'trên phần công ty nhận',
      nen: 'var(--color-warn-nen)',
      mauChu: 'var(--color-warn-chu)',
      dau: '−',
    },
  ];

  if (cuaCongTy) {
    o.push(
      {
        nhan: 'Content ID nhạc Miss Records',
        giaTri: dinhDangTien(dongTien.musicRevenue),
        moTa: 'Từ kênh bên ngoài dùng nhạc',
        nen: 'var(--color-nhan-nen)',
        mauChu: 'var(--color-nhan)',
        dau: '+',
      },
      {
        nhan: 'Phí kho nhạc hợp tác',
        /*
         * Kỳ chưa chốt phí thì hiện **"Theo hợp đồng"** cỡ chữ phụ.
         *
         * Bản cũ in chuỗi `[PHÍ THEO HĐ]` ở cỡ số 26px: dấu ngoặc vuông là ký
         * hiệu của người viết mã, không phải của người đọc báo cáo, và ở cỡ số
         * nó gãy ba dòng trên laptop.
         */
        giaTri: chuaNhapPhi ? 'Theo hợp đồng' : dinhDangTien(dongTien.musicLicenseCost),
        ...(chuaNhapPhi ? { laChu: true } : {}),
        moTa: chuaNhapPhi ? 'Đối tác kho nhạc · chưa chốt phí kỳ này' : 'Đối tác kho nhạc',
        nen: 'var(--color-header-bang)',
        mauChu: 'var(--color-chu)',
        dau: '−',
      },
      {
        nhan: 'Công ty thực nhận',
        giaTri: dinhDangTien(dongTien.companyNet),
        moTa: chuaNhapPhi ? 'trước phí kho nhạc' : 'sau mọi khoản',
        nen: 'var(--color-ok-nen)',
        mauChu: 'var(--color-ok-chu)',
        dau: '=',
      },
    );
  } else {
    /*
     * Dòng kết của bản theo kênh là **thực nhận từ kênh**, không phải "công ty
     * thực nhận": cộng thêm Content ID nhạc và trừ phí kho nhạc mới ra số của
     * công ty, mà hai khoản đó không gắn kênh nào nên người bị giới hạn kênh
     * không xem. Gọi đúng tên để không ai đọc số này thành dòng tiền công ty.
     */
    o.push({
      nhan: 'Kênh của bạn thực nhận',
      giaTri: dinhDangTien(dongTien.channelNet),
      moTa: 'sau thuế Mỹ, Network và VAT',
      nen: 'var(--color-ok-nen)',
      mauChu: 'var(--color-ok-chu)',
      dau: '=',
    });
  }

  return (
    <div className="mb-5">
      <KhoiMuc
        tieuDe={`Dòng tiền ${tenKy(dongTien.period)}`}
        moTa={
          cuaCongTy
            ? 'Đọc từ trên xuống là đúng thứ tự trừ, ra số thực nhận ở dòng cuối'
            : 'Chỉ các kênh bạn phụ trách · đọc từ trên xuống là đúng thứ tự trừ'
        }
      >
        {/*
          `@container/dong-tien`: dòng mô tả nằm **cùng dòng** với nhãn khi còn
          chỗ, xuống dòng riêng khi hẹp. Đo bề ngang của chính khối, không của
          khung nhìn — khối này nằm trong `KhoiMuc`, mà `KhoiMuc` có thể nằm
          trong một cột hẹp của trang khác.
        */}
        <ol data-khoi="dong-tien" className="@container/dong-tien flex flex-col">
          {o.map((muc, i) => {
            const cuoi = i === o.length - 1;

            /* Ba cỡ cho ba vai: dòng kết to nhất, ô "Theo hợp đồng" là **chữ**
               nên lấy cỡ chữ thường, còn lại là số. */
            let coSo = 'text-tieu-de-hop';
            if (cuoi) coSo = 'text-so-rat-lon';
            else if (muc.laChu === true) coSo = 'text-co-ban font-semibold';

            return (
              <li
                key={muc.nhan}
                data-o=""
                className={`flex items-center gap-3 ${
                  cuoi
                    ? /* Dòng kết: viền trên **dày** và nền nhạt — đây là con số
                         người ta mở trang để xem, phải tách khỏi các khoản trừ
                         bên trên mà không cần đọc chữ mới biết. */
                      '-mx-3 mt-2 rounded-o border-t-2 border-vien px-3 py-3'
                    : `py-2.5 ${i > 0 ? 'border-t border-vien-trong' : ''}`
                }`}
                style={cuoi ? { backgroundColor: muc.nen } : undefined}
              >
                <span
                  aria-hidden="true"
                  className="so-lieu w-5 shrink-0 text-center text-tieu-de-hop font-semibold"
                  style={{ color: muc.mauChu }}
                >
                  {muc.dau ?? ''}
                </span>

                {/* `data-nhan` ở **khối chứa chữ**, không ở từng dòng chữ: cái
                    cần đủ rộng là vùng chữ, còn "VAT" thì ngắn là đúng. */}
                <span
                  data-nhan=""
                  className="flex min-w-0 flex-1 flex-col @min-[560px]/dong-tien:flex-row @min-[560px]/dong-tien:items-baseline @min-[560px]/dong-tien:gap-2"
                >
                  <span
                    className={`truncate text-co-ban text-chu ${cuoi ? 'font-bold' : 'font-medium'}`}
                  >
                    {muc.nhan}
                  </span>
                  <span className="truncate text-phu text-chu-mo">{muc.moTa}</span>
                </span>

                <span
                  className={`so-lieu shrink-0 text-right font-bold whitespace-nowrap ${coSo}`}
                  style={{ color: muc.laChu === true ? 'var(--color-chu-phu)' : muc.mauChu }}
                >
                  {muc.giaTri}
                </span>
              </li>
            );
          })}
        </ol>

        {cuaCongTy ? (
          dongTien.companyNetVnd === null ? (
            <p className="mt-4 text-phu text-warn-chu">
              Chưa nhập tỷ giá cho kỳ này nên chưa quy đổi được VND.
            </p>
          ) : (
            <p className="mt-4 text-phu text-chu-mo">
              Quy đổi {dinhDangTien(dongTien.companyNetVnd, 'VND')} theo tỷ giá{' '}
              <span className="so-lieu">{soNguyen(Number(dongTien.exchangeRate))}</span> đã nhập.
            </p>
          )
        ) : (
          /*
           * Nói thẳng những gì **không** có trong khối này, thay vì để trống
           * hai dòng mà người đọc không biết là thiếu: Content ID kho nhạc, phí
           * kho nhạc và tỷ giá là khoản của cả công ty, không chia theo kênh.
           */
          <p className="mt-4 text-phu text-chu-mo">
            Content ID kho nhạc, phí kho nhạc và quy đổi VND là khoản của cả công ty, không chia
            theo kênh — chỉ quản trị xem.
          </p>
        )}
      </KhoiMuc>
    </div>
  );
}
